import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  descripcion: string;
  anio: number;
  cuatrimestre: number;
  fechaInicioCursada: string | null;
  fechaFinCursada: string | null;
  tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
  cantidadTemas: number;

  activo: boolean;
  profesorTitular: {
    id: number;
    nombre: string;
    apellido: string;
    email: string;
  };
  turnos: Array<{
    id: number;
    nombre: string;
    dia: string;
    horaInicio: string;
    horaFin: string;
    cupoMaximo: number;
    cupoOcupado: number;
  }>;
  docentes?: Array<{
    id: number;
    nombre: string;
    apellido: string;
    email: string;
    rol: string;
  }>;
}

interface Profesor {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  rol: string;
}

const CursosPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingCurso, setEditingCurso] = useState<Curso | null>(null);
  const [showDetails, setShowDetails] = useState<Curso | null>(null);
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    anio: new Date().getFullYear(),
    cuatrimestre: 1,
    fechaInicioCursada: '',
    fechaFinCursada: '',
    tipoEvaluacion: 'nota_completa' as 'nota_completa' | 'nota_por_tema',
    cantidadTemas: 1,

    activo: true,
    profesorTitularId: '',
    fechasExamen: [] as Array<{
      id?: number;
      fecha: string;
      tipo: string;
      descripcion: string;
      fechaMuestra?: string;
      activa: boolean;
    }>
  });

  // Fetch cursos
  const { data: cursos, isLoading, error } = useQuery({
    queryKey: ['cursos'],
    queryFn: async () => {
      const response = await axios.get(`/cursos`);
      return response.data;
    }
  });

  // Fetch profesores for dropdown
  const { data: profesores } = useQuery({
    queryKey: ['profesores'],
    queryFn: async () => {
      const response = await axios.get(`/profesores`);
      return response.data;
    }
  });

  // Create curso mutation
  const createCursoMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(`/cursos`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cursos'] });
      setShowModal(false);
      resetForm();
    }
  });

  // Update curso mutation
  const updateCursoMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await axios.patch(`/cursos/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cursos'] });
      setShowModal(false);
      setEditingCurso(null);
      resetForm();
    }
  });

  // Delete curso mutation
  const deleteCursoMutation = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`/cursos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cursos'] });
    }
  });

  const resetForm = () => {
    setFormData({
      nombre: '',
      codigo: '',
      descripcion: '',
      anio: new Date().getFullYear(),
      cuatrimestre: 1,
      fechaInicioCursada: '',
      fechaFinCursada: '',
      tipoEvaluacion: 'nota_completa',
      cantidadTemas: 1,

      activo: true,
      profesorTitularId: '',
      fechasExamen: []
    });
  };

  const handleEdit = async (curso: Curso) => {
    setEditingCurso(curso);
    
    // Cargar fechas de examen existentes
    let fechasExistentes = [];
    try {
      const response = await axios.get(`/fechas-examen/curso/${curso.id}?includeInactive=true`);
      fechasExistentes = response.data.map((fecha: any) => ({
        id: fecha.id, // Incluir el ID para poder actualizar
        fecha: fecha.fecha.split('T')[0], // Convertir a formato YYYY-MM-DD
        tipo: fecha.tipo,
        descripcion: fecha.descripcion || '',
        fechaMuestra: fecha.fechaMuestra ? fecha.fechaMuestra.split('T')[0] : '',
        activa: fecha.activa !== false,
      }));
    } catch (error) {
      console.warn('No se pudieron cargar las fechas de examen:', error);
    }
    
    setFormData({
      nombre: curso.nombre,
      codigo: curso.codigo,
      descripcion: curso.descripcion,
      anio: curso.anio,
      cuatrimestre: curso.cuatrimestre,
      fechaInicioCursada: curso.fechaInicioCursada ? curso.fechaInicioCursada.split('T')[0] : '',
      fechaFinCursada: curso.fechaFinCursada ? curso.fechaFinCursada.split('T')[0] : '',
      tipoEvaluacion: curso.tipoEvaluacion,
      cantidadTemas: curso.cantidadTemas,
      activo: curso.activo,
      profesorTitularId: curso.profesorTitular.id.toString(),
      fechasExamen: fechasExistentes
    });
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      profesorTitularId: parseInt(formData.profesorTitularId)
    };

    if (editingCurso) {
      updateCursoMutation.mutate({ id: editingCurso.id, data: submitData });
    } else {
      createCursoMutation.mutate(submitData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este curso?')) {
      deleteCursoMutation.mutate(id);
    }
  };

  if (isLoading) return <div className="text-center py-8">Cargando cursos...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error al cargar los cursos</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Cursos</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Nuevo Curso
        </button>
      </div>

      {/* Lista de cursos */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Cursos ({cursos?.length || 0})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Curso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profesor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Turnos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Docentes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(cursos) && cursos.map((curso: Curso) => (
                <tr key={curso.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{formatCursoNombreAnio(curso)}</div>
                      <div className="text-sm text-gray-500">{curso.codigo}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {curso.profesorTitular?.nombre} {curso.profesorTitular?.apellido}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{curso.turnos?.length || 0} turnos</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      curso.activo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {curso.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {curso.docentes?.length || 0} docente(s)
                    </div>
                    {curso.docentes && curso.docentes.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        {curso.docentes.slice(0, 2).map((d: any) => `${d.nombre} ${d.apellido}`).join(', ')}
                        {curso.docentes.length > 2 && ` +${curso.docentes.length - 2}`}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowDetails(curso)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(curso)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(curso.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear/editar curso */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCurso ? 'Editar Curso' : 'Nuevo Curso'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Código</label>
                    <input
                      type="text"
                      value={formData.codigo}
                      onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Descripción</label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      rows={3}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Año</label>
                    <input
                      type="number"
                      value={formData.anio}
                      onChange={(e) => setFormData({...formData, anio: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cuatrimestre</label>
                    <select
                      value={formData.cuatrimestre}
                      onChange={(e) => setFormData({...formData, cuatrimestre: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value={1}>Primer Cuatrimestre</option>
                      <option value={2}>Segundo Cuatrimestre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Inicio de Cursada</label>
                    <input
                      type="date"
                      value={formData.fechaInicioCursada}
                      onChange={(e) => setFormData({...formData, fechaInicioCursada: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fin de Cursada</label>
                    <input
                      type="date"
                      value={formData.fechaFinCursada}
                      onChange={(e) => setFormData({...formData, fechaFinCursada: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tipo de Evaluación</label>
                    <select
                      value={formData.tipoEvaluacion}
                      onChange={(e) => setFormData({...formData, tipoEvaluacion: e.target.value as 'nota_completa' | 'nota_por_tema'})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="nota_completa">Nota Completa</option>
                      <option value="nota_por_tema">Nota por Tema</option>
                    </select>
                  </div>
                  {formData.tipoEvaluacion === 'nota_por_tema' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Cantidad de Temas</label>
                      <input
                        type="number"
                        min="2"
                        max="3"
                        value={formData.cantidadTemas}
                        onChange={(e) => setFormData({...formData, cantidadTemas: parseInt(e.target.value)})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profesor Titular</label>
                    <select
                      value={formData.profesorTitularId}
                      onChange={(e) => setFormData({...formData, profesorTitularId: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Seleccionar profesor</option>
                      {Array.isArray(profesores) && profesores.map((profesor: Profesor) => (
                        <option key={profesor.id} value={profesor.id}>
                          {profesor.nombre} {profesor.apellido}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Fechas de examen */}
                <div className="border-t pt-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Fechas de Examen</h4>
                  {Array.isArray(formData.fechasExamen) && formData.fechasExamen.map((fecha, index) => (
                    <div key={index} className="flex gap-3 mb-3 p-3 bg-gray-50 rounded">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Fecha</label>
                        <input
                          type="date"
                          value={fecha.fecha}
                          onChange={(e) => {
                            const nuevasFechas = [...formData.fechasExamen];
                            nuevasFechas[index].fecha = e.target.value;
                            setFormData({...formData, fechasExamen: nuevasFechas});
                          }}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Tipo</label>
                        <select
                          value={fecha.tipo}
                          onChange={(e) => {
                            const nuevasFechas = [...formData.fechasExamen];
                            nuevasFechas[index].tipo = e.target.value;
                            setFormData({...formData, fechasExamen: nuevasFechas});
                          }}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="primera_fecha">Primera Fecha</option>
                          <option value="recuperatorio_1">Recuperatorio 1</option>
                          <option value="recuperatorio_2">Recuperatorio 2</option>
                          <option value="fecha_extra">Fecha Extra</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Descripción</label>
                        <input
                          type="text"
                          value={fecha.descripcion}
                          onChange={(e) => {
                            const nuevasFechas = [...formData.fechasExamen];
                            nuevasFechas[index].descripcion = e.target.value;
                            setFormData({...formData, fechasExamen: nuevasFechas});
                          }}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="Descripción opcional"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Fecha de Muestra</label>
                        <input
                          type="date"
                          value={fecha.fechaMuestra || ''}
                          onChange={(e) => {
                            const nuevasFechas = [...formData.fechasExamen];
                            nuevasFechas[index].fechaMuestra = e.target.value;
                            setFormData({...formData, fechasExamen: nuevasFechas});
                          }}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="Fecha de muestra opcional"
                        />
                      </div>
                      <div className="flex items-center pt-6">
                        <label className="block text-sm font-medium text-gray-700 mr-2">Activa</label>
                        <input
                          type="checkbox"
                          checked={fecha.activa !== false}
                          onChange={(e) => {
                            const nuevasFechas = [...formData.fechasExamen];
                            nuevasFechas[index].activa = e.target.checked;
                            setFormData({...formData, fechasExamen: nuevasFechas});
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => {
                            const nuevasFechas = formData.fechasExamen.filter((_, i) => i !== index);
                            setFormData({...formData, fechasExamen: nuevasFechas});
                          }}
                          className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        fechasExamen: [...formData.fechasExamen, {
                          fecha: '',
                          tipo: 'fecha_extra',
                          descripcion: '',
                          fechaMuestra: '',
                          activa: true
                        }]
                      });
                    }}
                    className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                  >
                    + Agregar Fecha
                  </button>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Curso Activo</label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCurso(null);
                      resetForm();
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createCursoMutation.isPending || updateCursoMutation.isPending}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createCursoMutation.isPending || updateCursoMutation.isPending ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para detalles del curso */}
      {showDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Detalles del Curso</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{showDetails.nombre}</h4>
                  <p className="text-sm text-gray-600">Código: {showDetails.codigo}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-700">{showDetails.descripcion}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Año: {showDetails.anio}</p>
                    <p className="text-sm font-medium text-gray-900">Cuatrimestre: {showDetails.cuatrimestre}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Tipo: {showDetails.tipoEvaluacion === 'nota_completa' ? 'Nota Completa' : 'Nota por Tema'}
                    </p>
                    {showDetails.tipoEvaluacion === 'nota_por_tema' && (
                      <p className="text-sm font-medium text-gray-900">Temas: {showDetails.cantidadTemas}</p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Profesor: {showDetails.profesorTitular?.nombre} {showDetails.profesorTitular?.apellido}
                  </p>
                </div>
                {showDetails.turnos && showDetails.turnos.length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Turnos:</h5>
                    <div className="space-y-2">
                      {Array.isArray(showDetails.turnos) && showDetails.turnos.map((turno) => (
                        <div key={turno.id} className="bg-gray-50 p-3 rounded">
                          <p className="text-sm font-medium">{turno.nombre}</p>
                          <p className="text-sm text-gray-600">
                            {turno.dia} {turno.horaInicio} - {turno.horaFin}
                          </p>
                          <p className="text-sm text-gray-600">
                            Cupo: {turno.cupoOcupado}/{turno.cupoMaximo}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowDetails(null)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CursosPage;
